// 測定ビュー（§14）。左=記録（タイマー・周回・ドロップカウンタ・臨時コスト）／右=実測（ライブサマリ・取り込み）。
// 1秒 tick は ctl.onTick 経由でタイマー表示とサマリ box だけを差し替える（構造は再描画しない）。
// カウンタ名などのテキスト入力は ctl.updateQuiet で保存のみ行い、フォーカスを維持する。
import { BASE_CURRENCY } from '../../config';
import { fromBase } from '../../currency/convert';
import { summarize } from '../../model/measure';
import type { MeasureSummary } from '../../model/measure';
import { applyImport, planImport } from '../../model/measure-import';
import type { ImportLineKind } from '../../model/measure-import';
import type { FarmSet } from '../../store/schema';
import type { MeasureDropCounter, MeasureExtraCost } from '../../store/measure-schema';
import { el, mount } from '../dom';
import { fmtMoney, fmtNum } from '../format';
import { buildCatalogDatalist, itemNameInput, num, unitCell, valueCell } from '../components/item-picker';
import type { PickerDeps } from '../components/item-picker';
import { currentRoute } from '../router';
import type { AppStore } from '../state';
import type { MeasureController } from '../measure-state';

const DATALIST_ID = 'poe2-catalog-measure';

export interface MeasureCallbacks {
  /** アクティブリーグの保存済みセット（リンク選択・取り込み対象）。 */
  savedSets(): FarmSet[];
  toast(msg: string, kind?: 'ok' | 'err'): void;
  /**
   * セットへの変更適用。計算ビューで同セットを開いていれば store 経由（署名変化→自動再計算）、
   * それ以外は localStorage から読んで適用・保存する。見つからなければ null。
   */
  applyToSet<T>(setId: string, fn: (set: FarmSet) => T): T | null;
}

function fmtClock(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtRunSec(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const KIND_LABEL: Record<ImportLineKind, string> = {
  'empirical-add': '実績に加算',
  'direct-convert': '実績+予測へ変換',
  'group-add': 'グループ一括',
  'new-line': '新規行を追加',
  orphan: '行なし→新規追加',
  blocked: '取り込み不可',
};

export class MeasureView {
  private ledger!: HTMLElement;
  private live!: HTMLElement;
  private summaryBox = el('div');
  private importBox = el('div');
  private timeEl: HTMLElement | null = null;
  private importOpen = false;
  private lastInputsKey = '';

  private readonly picker: PickerDeps = {
    rt: () => this.store.rateTable,
    onMutate: (fn) => this.ctl.updateQuiet(() => fn()),
    onStructure: () => this.renderAll(),
    datalistId: DATALIST_ID,
  };

  constructor(
    private readonly root: HTMLElement,
    private readonly store: AppStore,
    private readonly ctl: MeasureController,
    private readonly cb: MeasureCallbacks,
  ) {}

  mount(): void {
    this.ledger = el('div');
    this.live = el('div');
    mount(
      this.root,
      el('div', { class: 'desk' }, [
        el('section', { class: 'panel' }, [
          el('div', { class: 'panel-head' }, [el('h2', {}, '記録'), el('span', { class: 'num' }, 'RECORD')]),
          this.ledger,
        ]),
        el('section', { class: 'panel' }, [
          el('div', { class: 'panel-head' }, [el('h2', {}, '実測'), el('span', { class: 'num' }, 'LIVE')]),
          this.live,
        ]),
      ]),
    );
    this.ctl.subscribe(() => this.renderAll());
    this.ctl.onTick(() => this.renderLive());
    // 相場到着・リーグ切替・計算ビューのセット保存で候補が変わった時だけ作り直す。
    this.store.subscribe(() => {
      const key = `${this.store.activeLeague}|${this.store.rateTable?.fetchedAt}`;
      if (key !== this.lastInputsKey) {
        this.lastInputsKey = key;
        this.renderAll();
      }
    });
    window.addEventListener('keydown', (e) => this.onHotkey(e));
    this.renderAll();
  }

  // ── ホットキー（§14）: measure 表示中のみ・入力フォーカス中は無効 ──
  private onHotkey(e: KeyboardEvent): void {
    if (currentRoute() !== 'measure') return;
    const t = e.target as HTMLElement | null;
    if (t && (t.closest('input, textarea, select') || t.isContentEditable)) return;
    const s = this.ctl.session;
    if (!s) return;

    if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.ctl.undo();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === ' ') {
      // ボタンにフォーカスが残っていると Space がネイティブ click と二重発火するため常に抑止。
      e.preventDefault();
      this.ctl.addRun(1);
      return;
    }
    if (e.key === 'p' || e.key === 'P') {
      e.preventDefault();
      this.ctl.toggle();
      return;
    }
    const digit = parseInt(e.key, 10);
    if (digit >= 1 && digit <= 9) {
      const counter = s.drops[digit - 1];
      if (counter) {
        e.preventDefault();
        this.ctl.addDrop(counter.id, 1);
      }
    }
  }

  private linkedSet(): FarmSet | null {
    const id = this.ctl.session?.linkedSetId;
    if (!id) return null;
    return this.cb.savedSets().find((s) => s.id === id) ?? null;
  }

  private displayUnit(): string {
    return this.linkedSet()?.displayUnit ?? this.store.currentSet.displayUnit;
  }

  private money(base: number | null): string {
    const unit = this.displayUnit();
    const rt = this.store.rateTable;
    if (base == null || !rt) return `— ${unit}`;
    return fmtMoney(fromBase(base, unit, rt), unit);
  }

  // ── 描画 ──────────────────────
  private renderAll(): void {
    this.renderLedger();
    this.renderLivePanel();
  }

  private renderLedger(): void {
    const s = this.ctl.session;
    if (!s) {
      mount(this.ledger, this.buildToolbar(), el('div', { class: 'placeholder' }, 'セッションがありません。リンクするセットを選んで「新規測定」から始めてください。'));
      return;
    }
    mount(
      this.ledger,
      this.buildToolbar(),
      this.buildTimerBlock(s.runs),
      this.buildDropsBlock(),
      this.buildExtraCostBlock(),
      el('p', { class: 'hotkeys-hint' }, 'ホットキー: Space=周回+1 ／ 1〜9=ドロップ+1 ／ P=開始・停止 ／ Ctrl+Z=取り消し（入力中は無効）'),
      buildCatalogDatalist(this.store.rateTable, DATALIST_ID),
    );
  }

  private buildToolbar(): HTMLElement {
    const s = this.ctl.session;
    const sets = this.cb.savedSets();

    const sessionSel = el('select', {
      onchange: (e) => this.ctl.switchSession((e.target as HTMLSelectElement).value),
    }) as HTMLSelectElement;
    if (this.ctl.sessions.length === 0) sessionSel.append(el('option', { value: '' }, 'セッションなし'));
    for (const x of this.ctl.sessions) {
      sessionSel.append(el('option', { value: x.id, selected: x.id === s?.id }, x.name || '(無名の測定)'));
    }

    const linkSel = el('select', {
      onchange: (e) => {
        const v = (e.target as HTMLSelectElement).value;
        if (s) this.ctl.linkSet(sets.find((x) => x.id === v) ?? null);
      },
    }) as HTMLSelectElement;
    const currentLink = s?.linkedSetId ?? (sets.some((x) => x.id === this.store.currentSet.id) ? this.store.currentSet.id : '');
    linkSel.append(el('option', { value: '' }, 'リンクなし（自由測定）'));
    for (const set of sets) linkSel.append(el('option', { value: set.id, selected: set.id === currentLink }, set.name));

    const newBtn = el('button', {
      class: 'btn btn-primary',
      onclick: (e) => {
        const linked = sets.find((x) => x.id === linkSel.value);
        this.ctl.newSession(linked);
        (e.target as HTMLButtonElement).blur();
      },
    }, '新規測定');

    const rows: HTMLElement[] = [
      el('div', { class: 'toolbar' }, [sessionSel, newBtn, el('button', {
        class: 'btn',
        disabled: !s,
        onclick: () => { if (confirm('この測定セッションを削除しますか？')) this.ctl.deleteCurrentSession(); },
      }, '削除')]),
    ];
    if (!s) {
      // 空状態でも「どのセットにリンクして始めるか」は選べるようにする。
      rows.push(el('div', { class: 'toolbar' }, [el('label', { class: 'm-label' }, 'リンク'), linkSel]));
    }
    if (s) {
      rows.push(el('div', { class: 'toolbar' }, [
        el('input', { type: 'text', class: 'grow', value: s.name, placeholder: 'セッション名',
          oninput: (e) => this.ctl.updateQuiet((x) => { x.name = (e.target as HTMLInputElement).value; }) }),
        el('label', { class: 'm-label' }, 'リンク'),
        linkSel,
        el('button', { class: 'btn btn-ghost', title: 'リンクセットの行でカウンタが無いものを追加',
          onclick: () => {
            const set = this.linkedSet();
            if (!set) { this.cb.toast('リンクセットがありません', 'err'); return; }
            const added = this.ctl.addMissingCounters(set);
            this.cb.toast(added > 0 ? `${added} 件のカウンタを追加しました` : '追加するカウンタはありません');
            if (added > 0) this.renderAll();
          } }, '⟳ セットから同期'),
      ]));
    }
    return el('div', { class: 'block' }, rows);
  }

  private buildTimerBlock(runs: number): HTMLElement {
    const running = this.ctl.running();
    this.timeEl = el('div', { class: `timer-figure ${running ? 'running' : ''}` }, this.timerText());
    const toggleBtn = el('button', {
      class: `btn ${running ? '' : 'btn-primary'}`,
      onclick: (e) => { this.ctl.toggle(); (e.target as HTMLButtonElement).blur(); },
    }, running ? '⏸ 一時停止 (P)' : '▶ 開始 (P)');

    const runBtn = el('button', {
      class: 'btn btn-run',
      onclick: (e) => { this.ctl.addRun(1); (e.target as HTMLButtonElement).blur(); },
    }, [el('span', { class: 'run-label' }, '周回完了 +1'), el('span', { class: 'keycap' }, 'Space')]);

    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, 'タイマーと周回（一時停止中の時間は除外）'),
      el('div', { class: 'timer-row' }, [
        this.timeEl,
        el('div', { class: 'timer-side' }, [
          toggleBtn,
          el('button', { class: 'btn btn-ghost', disabled: !this.ctl.canUndo(),
            onclick: (e) => { this.ctl.undo(); (e.target as HTMLButtonElement).blur(); } }, '↩ 取り消し'),
        ]),
      ]),
      el('div', { class: 'run-row' }, [
        runBtn,
        el('div', { class: 'run-count' }, [
          el('span', { class: 'run-num' }, String(runs)),
          el('span', { class: 'run-unit' }, '周'),
          el('button', { class: 'iconbtn tiny', title: '周回 −1',
            onclick: (e) => { this.ctl.addRun(-1); (e.target as HTMLButtonElement).blur(); } }, '−1'),
        ]),
      ]),
    ]);
  }

  private timerText(): string {
    const s = this.ctl.session;
    if (!s) return '0:00:00';
    return fmtClock(Math.max(0, s.elapsedMs + (s.runningSince != null ? Date.now() - s.runningSince : 0)));
  }

  private buildDropsBlock(): HTMLElement {
    const s = this.ctl.session!;
    const rows = s.drops.map((c, i) => this.counterRow(c, i));
    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, 'ドロップ（当たりを数える）'),
      el('div', { class: 'counter-list' }, rows),
      el('button', { class: 'btn btn-add', onclick: () => {
        const c = this.ctl.addCounter(BASE_CURRENCY);
        if (c) this.expandedCounters.add(c.id);
        this.renderAll();
      } }, '＋ ドロップ品'),
    ]);
  }

  private readonly expandedCounters = new Set<string>();

  private counterRow(c: MeasureDropCounter, index: number): HTMLElement {
    const hotkey = index < 9 ? String(index + 1) : null;
    const open = this.expandedCounters.has(c.id);
    const fromSet = c.source.kind !== 'adhoc';

    const mainBtn = el('button', {
      class: 'btn btn-counter',
      onclick: (e) => { this.ctl.addDrop(c.id, 1); (e.currentTarget as HTMLButtonElement).blur(); },
    }, [
      hotkey ? el('span', { class: 'keycap' }, hotkey) : el('span'),
      el('span', { class: 'counter-name' }, c.name || '(無名ドロップ)'),
      el('span', { class: 'counter-count' }, `×${c.count}`),
    ]);

    const row = el('div', { class: 'counter-row' }, [
      mainBtn,
      el('button', { class: 'iconbtn tiny', title: '−1',
        onclick: (e) => { this.ctl.addDrop(c.id, -1); (e.currentTarget as HTMLButtonElement).blur(); } }, '−1'),
      el('button', { class: `iconbtn tiny ${open ? 'on' : ''}`, title: 'アイテム名・価値を編集',
        onclick: () => { if (open) this.expandedCounters.delete(c.id); else this.expandedCounters.add(c.id); this.renderAll(); } }, open ? '▾' : '✎'),
      el('button', { class: 'iconbtn danger tiny', title: 'カウンタ削除',
        onclick: () => { if (c.count === 0 || confirm(`「${c.name || '(無名)'}」(×${c.count}) を削除しますか？`)) { this.ctl.removeCounter(c.id); } } }, '×'),
    ]);
    if (fromSet) row.append(el('span', { class: 'badge badge-mode counter-src', title: 'リンクセットの行と対応' }, 'セット'));

    if (!open) return el('div', {}, [row]);
    return el('div', {}, [
      row,
      el('div', { class: 'counter-edit' }, [
        itemNameInput(c, this.picker),
        valueCell(c, this.picker),
        unitCell(c, this.picker),
      ]),
    ]);
  }

  private buildExtraCostBlock(): HTMLElement {
    const s = this.ctl.session!;
    const rows = s.extraCosts.map((c) => this.extraCostRow(c));
    return el('div', { class: 'block' }, [
      el('span', { class: 'eyebrow' }, '臨時コスト（想定外の消費・総額。周回比例のコストはリンクセットから自動計上）'),
      rows.length
        ? el('div', { class: 'tablewrap' }, [
            el('table', { class: 'ledger-table' }, [
              el('thead', {}, [el('tr', {}, [el('th', {}, 'アイテム'), el('th', {}, '価値'), el('th', {}, '単位'), el('th', { class: 'col-num' }, '個数'), el('th', { class: 'col-mini' })])]),
              el('tbody', {}, rows),
            ]),
          ])
        : el('span'),
      el('button', { class: 'btn btn-add', onclick: () => { this.ctl.addExtraCostRow(BASE_CURRENCY); } }, '＋ 臨時コスト'),
    ]);
  }

  private extraCostRow(c: MeasureExtraCost): HTMLElement {
    return el('tr', {}, [
      el('td', {}, [itemNameInput(c, this.picker)]),
      el('td', {}, [valueCell(c, this.picker)]),
      el('td', {}, [unitCell(c, this.picker)]),
      el('td', { class: 'col-num' }, [
        el('input', { type: 'number', class: 'count', value: fmtNum(c.count), title: '消費総数',
          oninput: (e) => this.ctl.updateQuiet(() => { c.count = num((e.target as HTMLInputElement).value); }) }),
      ]),
      el('td', { class: 'col-mini' }, [el('button', { class: 'iconbtn danger tiny', title: '削除',
        onclick: () => this.ctl.removeExtraCost(c.id) }, '×')]),
    ]);
  }

  // ── 右パネル（実測サマリ・取り込み） ──────────────────────
  private renderLivePanel(): void {
    const s = this.ctl.session;
    if (!s) {
      mount(this.live, el('div', { class: 'placeholder' }, '測定を開始すると実測サマリが表示されます。'));
      return;
    }
    mount(this.live, this.summaryBox, this.importBox);
    this.renderLive();
    this.renderImport();
  }

  /** tick ごとの部分更新（summaryBox のみ。入力要素は置かない）。 */
  private renderLive(): void {
    const s = this.ctl.session;
    if (!s) return;
    if (this.timeEl) this.timeEl.textContent = this.timerText();

    const linked = this.linkedSet();
    const sum = summarize(s, Date.now(), this.store.rateTable, linked);
    mount(this.summaryBox, ...this.buildSummary(sum, linked));
  }

  private buildSummary(sum: MeasureSummary, linked: FarmSet | null): HTMLElement[] {
    const metric = (k: string, val: string, sub?: string, neg = false) =>
      el('div', { class: 'metric' }, [
        el('div', { class: 'k' }, k),
        el('div', { class: `v ${neg ? 'neg' : ''}` }, val),
        sub ? el('div', { class: 'm-sub' }, sub) : el('span'),
      ]);

    const netBase = sum.incomeBase - sum.costBase;
    const out: HTMLElement[] = [
      el('div', { class: 'metrics' }, [
        metric('実測時間（停止除く）', fmtClock(sum.elapsedMs)),
        metric('平均周回時間', fmtRunSec(sum.avgRunSec), linked ? `セット想定 ${fmtRunSec(sum.expectedRunSec)}` : undefined),
        metric('実測収入', this.money(sum.incomeBase)),
        metric('実測コスト', this.money(sum.costBase), linked ? 'セットのコスト×周 + 臨時' : '臨時コストのみ'),
        metric('実測損益', this.money(netBase), undefined, netBase < 0),
        metric('実測時給', this.money(sum.measuredHourlyBase), sum.expectedHourlyBase != null ? `セット想定 ${this.money(sum.expectedHourlyBase)}` : undefined, (sum.measuredHourlyBase ?? 0) < 0),
      ]),
    ];

    if (sum.missingRates.length) {
      out.push(el('p', { class: 'ci warn-text' }, `レート未取得のため除外: ${sum.missingRates.join('、')}（手動価値を入力すると集計に入ります）`));
    }

    if (sum.perDrop.length) {
      out.push(el('div', { class: 'tablewrap' }, [
        el('table', { class: 'ledger-table sub' }, [
          el('thead', {}, [el('tr', {}, [el('th', {}, 'ドロップ'), el('th', { class: 'col-num' }, 'k'), el('th', { class: 'col-num' }, 'λ実測'), el('th', { class: 'col-num' }, 'λ想定')])]),
          el('tbody', {}, sum.perDrop.map((d) =>
            el('tr', {}, [
              el('td', {}, d.name || '(無名)'),
              el('td', { class: 'col-num' }, String(d.count)),
              el('td', { class: 'col-num' }, fmtNum(d.measuredLambda)),
              el('td', { class: 'col-num' }, d.expectedLambda == null ? '—' : fmtNum(d.expectedLambda)),
            ]))),
        ]),
      ]));
    }
    return out;
  }

  // ── 取り込み（§14: ウォーターマーク差分・確認つき） ──────────────────────
  private renderImport(): void {
    const s = this.ctl.session;
    if (!s) {
      mount(this.importBox);
      return;
    }
    const linked = this.linkedSet();
    if (!linked) {
      mount(this.importBox, el('div', { class: 'block' }, [
        el('span', { class: 'eyebrow' }, 'セットへの取り込み'),
        el('p', { class: 'ci' }, 'ファームセットをリンクすると、測定した周回数・ドロップ実績をセットの実績 (N, k) へ差分で取り込めます。'),
      ]));
      return;
    }

    const plan = planImport(s, linked, Date.now());
    const head = el('span', { class: 'eyebrow' }, `セット「${linked.name}」へ実績を取り込む`);

    if (!this.importOpen) {
      mount(this.importBox, el('div', { class: 'block' }, [
        head,
        el('button', { class: 'btn btn-primary', disabled: plan.empty,
          onclick: () => { this.importOpen = true; this.renderImport(); } },
          plan.empty ? '取り込める差分がありません' : '⇪ 取り込みプレビューを開く'),
        el('p', { class: 'ci' }, '差分方式: 前回取り込み以降の (ΔN, Δk) だけを加算するため、二重取り込みは起きません。'),
      ]));
      return;
    }

    const checks = new Map<string, HTMLInputElement>();
    const lineRows = plan.lines.map((l) => {
      const box = el('input', { type: 'checkbox' }) as HTMLInputElement;
      box.checked = l.selectable && l.kind !== 'orphan' && !(l.kind === 'new-line' && l.deltaK === 0);
      box.disabled = !l.selectable;
      checks.set(l.key, box);
      return el('tr', { class: l.selectable ? '' : 'row-blocked' }, [
        el('td', {}, [box]),
        el('td', {}, l.name),
        el('td', {}, [el('span', { class: 'badge badge-mode' }, KIND_LABEL[l.kind])]),
        el('td', { class: 'col-num' }, `+${l.deltaN}`),
        el('td', { class: 'col-num' }, `+${l.deltaK}`),
        el('td', { class: 'import-note' }, l.note),
      ]);
    });

    const timeCheck = el('input', { type: 'checkbox' }) as HTMLInputElement;
    timeCheck.checked = plan.timeProposal != null;
    const timeRow = plan.timeProposal
      ? el('label', { class: 'import-time' }, [
          timeCheck,
          el('span', {}, ` 周回時間 t を実測平均へ更新: ${plan.timeProposal.current} 秒 → ${plan.timeProposal.measured} 秒`),
        ])
      : el('p', { class: 'ci' }, '周回時間 t の更新提案はありません（実測平均と同値、または周回なし）。');

    const apply = () => {
      const selected = new Set<string>();
      for (const [key, box] of checks) if (box.checked && !box.disabled) selected.add(key);
      const result = this.cb.applyToSet(linked.id, (set) =>
        applyImport(s, set, { selected, applyTime: plan.timeProposal != null && timeCheck.checked }, Date.now()),
      );
      if (!result) {
        this.cb.toast('取り込み先のセットが見つかりませんでした', 'err');
        return;
      }
      // ウォーターマーク・source 付け替えを永続化し、取り込み跨ぎの Undo を無効化。
      // importOpen は update の emit で再描画される前に畳んでおく。
      this.importOpen = false;
      this.ctl.clearUndo();
      this.ctl.update(() => {});
      const parts = [
        result.updatedLines > 0 ? `${result.updatedLines} 行を更新` : '',
        result.addedLines > 0 ? `${result.addedLines} 行を新規追加` : '',
        `+${result.maxDeltaN} 周 / +${result.totalDeltaK} 個`,
        result.timeApplied ? 't 更新' : '',
      ].filter(Boolean);
      this.cb.toast(`「${linked.name}」へ取り込みました（${parts.join('、')}）`);
    };

    mount(this.importBox, el('div', { class: 'block' }, [
      head,
      plan.lines.length
        ? el('div', { class: 'tablewrap' }, [
            el('table', { class: 'ledger-table sub' }, [
              el('thead', {}, [el('tr', {}, [el('th'), el('th', {}, 'ドロップ'), el('th', {}, '処理'), el('th', { class: 'col-num' }, 'ΔN'), el('th', { class: 'col-num' }, 'Δk'), el('th', {}, '内容')])]),
              el('tbody', {}, lineRows),
            ]),
          ])
        : el('p', { class: 'ci' }, 'ドロップの差分はありません。'),
      timeRow,
      el('p', { class: 'ci' }, '臨時コストはセットへ書き戻しません（実測時給の計算のみに反映。周回比例コストはセット定義が正）。'),
      el('div', { class: 'toolbar' }, [
        el('button', { class: 'btn btn-primary', disabled: plan.empty, onclick: apply }, '適用'),
        el('button', { class: 'btn', onclick: () => { this.importOpen = false; this.renderImport(); } }, '閉じる'),
      ]),
    ]));
  }
}
